// src/pages/AboutPage.jsx
import { FaUsers, FaTrophy, FaHandshake, FaGlobe } from "react-icons/fa";
import missionImg from "../assets/blog/mission.jpeg";

function AboutPage() {
  const stats = [
    { number: "15+", label: "Team Members" },
    { number: "4+", label: "Divisions" },
    { number: "5+", label: "Countries Represented" },
    { number: "50+", label: "Tournament Wins" },
  ];

  const values = [
    {
      icon: FaUsers,
      title: "Representation",
      description: "We proudly represent Caribbean gamers on the global stage, showcasing the talent and passion of our region."
    },
    {
      icon: FaTrophy,
      title: "Excellence",
      description: "We strive for excellence in everything we do, from competitive play to content creation and community building."
    },
    {
      icon: FaHandshake,
      title: "Respect",
      description: "We treat every opponent, teammate, and community member with respect, fostering a positive gaming environment."
    },
    {
      icon: FaGlobe,
      title: "Community",
      description: "We build bridges across the Caribbean gaming community, connecting players and fans through shared passion."
    },
  ];

  return (
    <main className="bg-black min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-red-900/20 to-black"></div>
        <div className="max-w-6xl mx-auto relative z-10">
          <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tight text-white mb-6">
            About <span className="text-red-600">Prime Valor</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl">
            Prime Valor is a Caribbean-based competitive gaming organization dedicated to elevating
            the region's presence on the global stage.
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 bg-gray-950">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-black text-red-600 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-400 uppercase tracking-wide text-sm">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 uppercase">
                Our Mission
              </h2>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Founded with a vision to unite and elevate Caribbean gamers, Prime Valor
                has grown from a small group of passionate players into a competitive force across
                multiple gaming titles.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed">
                We believe in the untapped potential of Caribbean gaming talent. Our mission is to
                provide a platform where skilled players can compete at the highest levels, create
                engaging content, and represent our region with pride on the global stage.
              </p>
            </div>
            <div className="relative">
              <img
                src={missionImg}
                alt="Prime Valor Mission"
                className="w-full rounded-lg object-cover shadow-2xl shadow-red-900/20"
              />
              <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-red-600/30 rounded-lg -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-6 bg-gray-950">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 uppercase text-center">
            Our Values
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-gray-900 p-6 rounded-lg border border-gray-800 hover:border-red-600/50 transition-all duration-300 group"
              >
                <value.icon className="text-4xl text-red-600 mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-xl font-bold text-white mb-3 uppercase">
                  {value.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* History Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 uppercase text-center">
            Our Journey
          </h2>
          <div className="space-y-8">
            {[
              { year: "2024", title: "Prime Valor Founded", description: "Prime Valor was founded with a core group of dedicated Caribbean gamers." },
              { year: "2024", title: "First Community Tournament Hosted", description: "Hosted our first community tournament, bringing together players from across the region." },
              { year: "2024", title: "Prime Valor Academy Launch", description: "Launched Prime Valor Academy to develop and train the next generation of Caribbean talent." },
              { year: "2024", title: "Prime Valor Merchandise Launch", description: "Released our first official merchandise line, letting the community rep Prime Valor." },
              { year: "2025", title: "Prime Valor Tournaments Streamed Live", description: "Began streaming Prime Valor tournaments live, bringing competitive Caribbean gaming to a wider audience." },
              { year: "2026", title: "Prime Valor Official Website Launch", description: "Launched the official Prime Valor website, establishing our permanent online home." },
              { year: "2026", title: "Prime Valor's Very First Esports Roster", description: "Built their very first competitive roster to showcase the talent we have outside of the region." },
              { year: "2026", title: "Officially Started Using X", description: "Officially started using X (formerly known as Twitter)." },
              { year: "2026", title: "Launched the Official Caribbean Cup", description: "Launched the official Caribbean Cup to accommodate country vs country tournaments." },
            ].map((milestone, index) => (
              <div key={index} className="flex gap-6 items-start">
                <div className="flex-shrink-0 w-20 text-right">
                  <span className="text-red-600 font-bold">{milestone.year}</span>
                </div>
                <div className="w-px bg-red-600/30 relative">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3 h-3 bg-red-600 rounded-full"></div>
                </div>
                <div className="flex-1 pb-8">
                  <h3 className="text-xl font-bold text-white mb-2">{milestone.title}</h3>
                  <p className="text-gray-400">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

export default AboutPage;
